import React, { useCallback, useLayoutEffect, useMemo } from "react";
import { DateRange } from "@common/helpers/date";
import { useConstant } from "@hooks/useConstant";
import { Field } from "@design-system/Field/Field";
import { Picker } from "@design-system/Picker/Picker.lib";
import { DatePicker } from "@design-system/DatePicker/DatePicker";
import {
    parseDateOnly,
    toDateOnly,
} from "@design-system/DatePicker/DatePicker.lib";
import type {
    DateOnlyRange,
    DatePickerPreset,
} from "@design-system/DatePicker/DatePicker.types";
import { DATE_RANGE_PRESETS_BY_ID, VISIBLE_PRESETS } from "@helpers/date";
import { useDateRangePicker } from "@design-system/DatePicker/useDateRangePicker";
import styles from "./AggregatedDateFilter.module.scss";

const ALL_TIME_PRESET_ID = "all_time";

interface DateFilter {
    start?: Date;
    end?: Date;
}

/** A preset the caller stores by id and resolves itself; `value` is its range today. */
export interface RelativeDatePreset<
    Id extends string,
> extends DatePickerPreset<DateOnlyRange> {
    id: Id;
}

interface RelativeDatePresets<Id extends string> {
    presets: ReadonlyArray<RelativeDatePreset<Id>>;
    selectedId: Id | undefined;
    onSelect(id: Id): void;
}

const NO_RELATIVE_PRESETS: ReadonlyArray<RelativeDatePreset<string>> = [];

interface Props<Id extends string> {
    filters: DateFilter;
    onChange(value: DateFilter): void;
    requireSelection?: boolean;
    maxDate?: Date;
    /**
     * Preset ids to display in the Period select. Defaults to VISIBLE_PRESETS
     * (past-oriented). Pass WORK_DATE_VISIBLE_PRESETS for filters that should
     * also offer "This week" and allow selecting future dates.
     */
    visiblePresets?: string[];
    /**
     * Listed first in the Period select, replacing fixed presets with the same
     * id. Picking one calls `relativePresets.onSelect` instead of `onChange`.
     */
    relativePresets?: RelativeDatePresets<Id>;
}

export function AggregatedDateFilter<Id extends string = string>({
    filters,
    onChange,
    requireSelection = false,
    maxDate: maxDateProp,
    visiblePresets = VISIBLE_PRESETS,
    relativePresets,
}: Props<Id>) {
    const relativePresetItems = relativePresets?.presets ?? NO_RELATIVE_PRESETS;
    const stableFilters = React.useRef(filters);
    const stableOnChange = React.useRef(onChange);

    useLayoutEffect(() => {
        stableOnChange.current = onChange;
        stableFilters.current = filters;
    }, [filters, onChange]);

    const onDateRangeChanged = useCallback((newValue: Partial<DateRange>) => {
        const { start, end } = stableFilters.current;

        if (
            newValue.start?.getTime() !== start?.getTime() ||
            newValue.end?.getTime() !== end?.getTime()
        ) {
            stableOnChange.current(newValue);
        }
    }, []);

    const {
        start,
        setStart,
        end,
        setEnd,
        maxDate,
        activePreset,
        selectPreset,
        presets,
    } = useDateRangePicker({
        onChange: onDateRangeChanged,
        initialValue: filters,
        visiblePresets,
        requireSelection,
        maxDate: maxDateProp,
    });

    const rangeValue = useMemo(
        (): DateOnlyRange | undefined =>
            start || end
                ? { from: toDateOnly(start), to: toDateOnly(end) }
                : undefined,
        [start, end],
    );

    const handleRangeChange = useCallback(
        (next: DateOnlyRange | undefined) => {
            setStart(parseDateOnly(next?.from));
            setEnd(parseDateOnly(next?.to));
        },
        [setStart, setEnd],
    );

    const today = useConstant(() => new Date());
    const presetOptions = useMemo(() => {
        const options = [
            ...relativePresetItems.map((preset) => ({
                value: preset.id,
                label: preset.label,
            })),
            ...presets
                .filter(
                    (preset) =>
                        !relativePresetItems.some(
                            (relativePreset) => relativePreset.id === preset.id,
                        ),
                )
                .map((preset) => ({
                    value: preset.id,
                    label:
                        (preset.labelFn
                            ? preset.labelFn(today)
                            : preset.label) ?? "",
                })),
        ];

        if (!requireSelection) {
            options.push({
                value: ALL_TIME_PRESET_ID,
                label: "All time",
            });
        }

        return options;
    }, [presets, relativePresetItems, requireSelection, today]);

    const handlePresetChange = useCallback(
        (value: string) => {
            const relativePreset = relativePresets?.presets.find(
                (preset) => preset.id === value,
            );
            if (!relativePresets || !relativePreset) {
                selectPreset(DATE_RANGE_PRESETS_BY_ID[value]);
                return;
            }
            const nextRange = {
                start: parseDateOnly(relativePreset.value.from),
                end: parseDateOnly(relativePreset.value.to),
            };
            // The calendar shows the range, but the caller stores the id, so
            // the range must not echo back through onChange as explicit dates.
            stableFilters.current = nextRange;
            setStart(nextRange.start);
            setEnd(nextRange.end);
            relativePresets.onSelect(relativePreset.id);
        },
        [relativePresets, selectPreset, setStart, setEnd],
    );

    const isActivePresetReplaced = relativePresetItems.some(
        (preset) => preset.id === activePreset,
    );
    let displayedPreset: string | undefined;
    if (relativePresets?.selectedId) {
        displayedPreset = relativePresets.selectedId;
    } else if (activePreset && !isActivePresetReplaced) {
        displayedPreset = activePreset;
    } else if (!start && !end) {
        displayedPreset = ALL_TIME_PRESET_ID;
    }

    return (
        <div className={styles.filter}>
            <section className={styles.presets}>
                <Field
                    label="Period"
                    control="button"
                    className={styles.periodField}
                >
                    <Picker.Select
                        items={presetOptions}
                        value={displayedPreset ?? null}
                        onValueChange={(value) => {
                            if (value) {
                                handlePresetChange(value);
                            }
                        }}
                        placeholder="Custom dates"
                        trigger={
                            <Picker.Button variant="primary" size="xl" block />
                        }
                    />
                </Field>
            </section>
            <section className={styles.calendar}>
                <DatePicker
                    mode="monthRange"
                    variant="embedded"
                    value={rangeValue}
                    onValueChange={handleRangeChange}
                    maxDate={toDateOnly(maxDate)}
                    showAdjacentYearButtons
                    clearable={false}
                />
            </section>
        </div>
    );
}
